import { cityApi, getCitySettings, getDialogueTree, saveDialogueTree, updateCitySettings, updateCityText } from '@/services/main/mundoGamerCity.service'

const RESOURCES = [
	'districts',
	'npcs',
	'features',
	'missions',
	'coinSpawns',
	'easterEggs',
	'seasonalEvents',
	'profiles',
	'dialogues',
	'texts',
]

const emptyList = () => ({ data: [], meta: { total: 0 } })

/**
 * Laravel paginate() returns { data, total, ... } at the top level.
 * Normalize to the { data, meta: { total } } shape the v-data-tables expect.
 */
const normalize = (payload) => {
	if (payload && Array.isArray(payload.data)) {
		return { data: payload.data, meta: { total: payload.total ?? payload.meta?.total ?? payload.data.length } }
	}
	if (Array.isArray(payload)) return { data: payload, meta: { total: payload.length } }

	return emptyList()
}

const state = {
	lists: RESOURCES.reduce((acc, r) => ({ ...acc, [r]: emptyList() }), {}),
	statuses: [...RESOURCES, 'settings'].reduce((acc, r) => ({ ...acc, [r]: 'init' }), {}),
}

export default {
	namespaced: true,
	state,
	getters: {
		getList: (s) => (resource) => s.lists[resource] || emptyList(),
		getStatus: (s) => (resource) => s.statuses[resource] || 'init',
	},
	mutations: {
		SET_LIST(s, { resource, payload }) {
			s.lists = { ...s.lists, [resource]: normalize(payload) }
		},
		SET_STATUS(s, { resource, status }) {
			s.statuses = { ...s.statuses, [resource]: status }
		},
	},
	actions: {
		async fetchList({ commit }, { resource, params = {} }) {
			commit('SET_STATUS', { resource, status: 'pending' })
			try {
				const { data } = await cityApi[resource].list(params)
				commit('SET_LIST', { resource, payload: data })

				return data
			} finally {
				commit('SET_STATUS', { resource, status: 'init' })
			}
		},
		async fetchItem(_ctx, { resource, id }) {
			const { data } = await cityApi[resource].get(id)

			return data
		},
		async saveItem({ commit }, { resource, params }) {
			commit('SET_STATUS', { resource, status: 'pending' })
			try {
				const { data } = await cityApi[resource].save(params)

				return data
			} finally {
				commit('SET_STATUS', { resource, status: 'init' })
			}
		},
		async updateItem({ commit }, { resource, id, params }) {
			commit('SET_STATUS', { resource, status: 'pending' })
			try {
				const { data } = await cityApi[resource].update(id, params)

				return data
			} finally {
				commit('SET_STATUS', { resource, status: 'init' })
			}
		},
		async deleteItem({ commit }, { resource, id }) {
			commit('SET_STATUS', { resource, status: 'pending' })
			try {
				const { data } = await cityApi[resource].remove(id)

				return data
			} finally {
				commit('SET_STATUS', { resource, status: 'init' })
			}
		},
		async fetchSettings({ commit }) {
			commit('SET_STATUS', { resource: 'settings', status: 'pending' })
			try {
				const { data } = await getCitySettings()

				return data.settings || []
			} finally {
				commit('SET_STATUS', { resource: 'settings', status: 'init' })
			}
		},
		async saveSettings({ commit }, settings) {
			commit('SET_STATUS', { resource: 'settings', status: 'pending' })
			try {
				const { data } = await updateCitySettings(settings)

				return data.settings || []
			} finally {
				commit('SET_STATUS', { resource: 'settings', status: 'init' })
			}
		},
		async updateText({ commit }, params) {
			commit('SET_STATUS', { resource: 'texts', status: 'pending' })
			try {
				const { data } = await updateCityText(params)

				return data
			} finally {
				commit('SET_STATUS', { resource: 'texts', status: 'init' })
			}
		},
		async fetchDialogueTree(_ctx, id) {
			const { data } = await getDialogueTree(id)

			return data
		},
		async saveDialogueTree({ commit }, { id, nodes }) {
			commit('SET_STATUS', { resource: 'dialogues', status: 'pending' })
			try {
				const { data } = await saveDialogueTree(id, nodes)

				return data
			} finally {
				commit('SET_STATUS', { resource: 'dialogues', status: 'init' })
			}
		},
	},
}
